
import { defineComponent, PropType } from "vue";
import Swal from "sweetalert2/dist/sweetalert2.js";
import {
  deleteContentMedia,
  getContentMediaStatus,
  listReelCatalogMedia,
  uploadReelCatalogMedia
} from "@/api/ghlContent.api.js";

const REEL_CATALOG_IMAGE_MIMES = new Set(["image/jpeg", "image/png", "image/webp"]);

function isReelCatalogFileAllowed(file: File) {
  const mime = String(file.type || "").trim().toLowerCase();
  if (mime.startsWith("video/")) return true;
  return REEL_CATALOG_IMAGE_MIMES.has(mime);
}

export default defineComponent({
  name: "reel-catalog-library",
  props: {
    modelValue: {
      type: Array as PropType<number[]>,
      default: () => []
    },
    selectable: {
      type: Boolean,
      default: true
    }
  },
  emits: ["update:modelValue"],
  data() {
    return {
      loading: true,
      uploading: false,
      files: [] as any[],
      usedBytes: 0,
      limitBytes: 0,
      maxFiles: 20,
      pollTimer: null as ReturnType<typeof setInterval> | null
    };
  },
  computed: {
    selectedIds(): number[] {
      return Array.isArray(this.modelValue) ? this.modelValue : [];
    },
    storagePercent(): number {
      if (!this.limitBytes) return 0;
      return Math.min(100, Math.round((this.usedBytes / this.limitBytes) * 100));
    },
    hasPendingFiles(): boolean {
      return this.files.some((file) => file.status === "pending");
    }
  },
  async mounted() {
    await this.loadCatalog();
    this.startPollingIfNeeded();
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    canSelect(file: any) {
      return file?.status === "ready";
    },
    isSelected(id: number) {
      return this.selectedIds.includes(Number(id));
    },
    toggleSelection(id: number, event: Event) {
      const checked = (event.target as HTMLInputElement).checked;
      const normalizedId = Number(id);
      const next = checked
        ? this.selectedIds.includes(normalizedId)
          ? this.selectedIds
          : this.selectedIds.concat(normalizedId)
        : this.selectedIds.filter((entry) => entry !== normalizedId);
      this.$emit("update:modelValue", next);
    },
    async loadCatalog() {
      this.loading = true;
      try {
        const response = await listReelCatalogMedia({ limit: 50, offset: 0 });
        const data = response.data || {};
        this.files = data.files || [];
        this.usedBytes = data.usedBytes || 0;
        this.limitBytes = data.limitBytes || 0;
        this.maxFiles = Number(data.maxFiles) || 20;
        this.pruneInvalidSelections();
      } catch {
        this.files = [];
      } finally {
        this.loading = false;
      }
    },
    pruneInvalidSelections() {
      const readyIds = new Set(
        this.files.filter((file) => file.status === "ready").map((file) => Number(file.id))
      );
      const next = this.selectedIds.filter((id) => readyIds.has(id));
      if (next.length !== this.selectedIds.length) {
        this.$emit("update:modelValue", next);
      }
    },
    triggerUpload() {
      const input = this.$refs.fileInput as HTMLInputElement;
      input?.click();
    },
    async onFileSelected(event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      input.value = "";
      if (!file) return;

      if (!isReelCatalogFileAllowed(file)) {
        Swal.fire({
          title: "",
          text: "Only videos and photos (JPEG, PNG, WebP) can be uploaded to the community reel catalog.",
          icon: "warning",
          confirmButtonClass: "btn btn-secondary"
        });
        return;
      }

      this.uploading = true;
      try {
        await uploadReelCatalogMedia(file);
        await this.loadCatalog();
        this.startPollingIfNeeded();
      } catch (e) {
        Swal.fire({
          title: "",
          text: e?.response?.data?.message || "Unable to upload file.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
      } finally {
        this.uploading = false;
      }
    },
    startPollingIfNeeded() {
      if (!this.hasPendingFiles) {
        this.stopPolling();
        return;
      }
      if (this.pollTimer) return;
      this.pollTimer = setInterval(() => {
        this.pollPendingFiles();
      }, 3000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async pollPendingFiles() {
      const pending = this.files.filter((file) => file.status === "pending");
      if (!pending.length) {
        this.stopPolling();
        return;
      }

      let changed = false;
      for (const file of pending) {
        try {
          const response = await getContentMediaStatus(file.id);
          const updated = response.data || {};
          const index = this.files.findIndex((entry) => entry.id === file.id);
          if (index >= 0 && updated.status && updated.status !== this.files[index].status) {
            this.files[index] = { ...this.files[index], ...updated };
            changed = true;
          }
        } catch {
          // keep polling
        }
      }

      if (changed) {
        await this.loadCatalog();
      }
      if (!this.hasPendingFiles) {
        this.stopPolling();
      }
    },
    formatBytes(bytes: number) {
      if (!bytes) return "0 B";
      const units = ["B", "KB", "MB", "GB"];
      let value = bytes;
      let unit = 0;
      while (value >= 1024 && unit < units.length - 1) {
        value /= 1024;
        unit += 1;
      }
      return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
    },
    thumbUrl(file: any) {
      if (file.thumbnailUrl) return file.thumbnailUrl;
      const mime = String(file.mimeType || "").trim().toLowerCase();
      if (mime.startsWith("image/") && file.publicUrl) return file.publicUrl;
      return null;
    },
    statusClass(status: string) {
      if (status === "ready") return "badge-light-success";
      if (status === "pending") return "badge-light-warning";
      if (status === "failed") return "badge-light-danger";
      return "badge-light";
    },
    async confirmDelete(file: any) {
      const result = await Swal.fire({
        title: "Delete clip?",
        text: `Remove "${file.originalFilename}" from your community reel catalog?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Delete",
        cancelButtonText: "Cancel",
        confirmButtonClass: "btn btn-danger",
        cancelButtonClass: "btn btn-light"
      });
      if (!result.isConfirmed) return;

      try {
        await deleteContentMedia(file.id);
        const next = this.selectedIds.filter((id) => id !== Number(file.id));
        if (next.length !== this.selectedIds.length) {
          this.$emit("update:modelValue", next);
        }
        await this.loadCatalog();
        this.startPollingIfNeeded();
        Swal.fire({
          title: "",
          text: "Clip deleted.",
          icon: "success",
          confirmButtonClass: "btn btn-secondary"
        });
      } catch (e) {
        Swal.fire({
          title: "",
          text: e?.response?.data?.message || "Unable to delete clip.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
      }
    }
  }
});
